import React, { useState, useEffect } from 'react';
import {
  Link as RouterLink,
  useParams,
  useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import {
  Container,
  Box,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Avatar,
  Grid,
} from '@mui/material';
import { LocalizedAlert as Alert, LocalizedButton as Button, LocalizedChip as Chip } from '../components/i18n/LocalizedMui';
import Typography from '../components/i18n/LocalizedTypography';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DateRangeIcon from '@mui/icons-material/DateRange';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatDisplayDate } from '../utils/dateDisplay';

const MarkdownContainer = styled('section')(({ theme }) => ({
  '& h1, & h2, & h3, & h4, & h5, & h6': {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
    fontWeight: 600
  },
  '& p': {
    marginBottom: theme.spacing(2),
    lineHeight: 1.8,
    fontSize: '1rem',
    color: '#333'
  },
  '& a': {
    color: theme.palette.primary.main,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  '& ul, & ol': {
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(3)
  },
  '& li': {
    marginBottom: theme.spacing(1),
    lineHeight: 1.8
  },
  '& blockquote': {
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    paddingLeft: theme.spacing(2),
    marginLeft: 0,
    marginBottom: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(0.5)
  },
  '& code': {
    backgroundColor: '#f5f5f5',
    padding: '2px 6px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '0.9em'
  },
  '& pre': {
    backgroundColor: theme.palette.primary.main,
    color: '#fff',
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    overflow: 'auto',
    marginBottom: theme.spacing(2),
    '& code': {
      backgroundColor: 'transparent',
      color: '#fff',
      padding: 0
    }
  },
  '& img': {
    maxWidth: '100%',
    height: 'auto',
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(2)
  }
}));

interface BlogPostData {
  _id: string;
  title: string;
  slug: string;
  description: string;
  content: string;
  author: string;
  tags: string[];
  category: string;
  readingTime: number;
  views: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  language?: string;
  aiSummary?: string;
  keyTakeaways?: string[];
  faq?: Array<{
    question: string;
    answer: string;
  }>;
  seo?: {
    title: string;
    description: string;
    keywords: string;
    canonicalUrl: string;
  };
}

interface RelatedPost {
  _id: string;
  title: string;
  slug: string;
  description: string;
  createdAt: string;
  readingTime: number;
}

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://kundlistarstudio.com';

const normalizeText = (value: string) => value.replace(/\s+/g, ' ').trim().toLowerCase();

const stripDuplicateLeadContent = (html: string, title: string, description: string) => {
  if (typeof window === 'undefined') return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;

  const firstContentElement = () => Array.from(body.children).find((element) => normalizeText(element.textContent || ''));
  const firstElement = firstContentElement();

  if (
    firstElement &&
    /^H[1-6]$/.test(firstElement.tagName) &&
    normalizeText(firstElement.textContent || '') === normalizeText(title)
  ) {
    firstElement.remove();
  }

  const nextElement = firstContentElement();
  if (
    nextElement?.tagName === 'P' &&
    normalizeText(nextElement.textContent || '') === normalizeText(description)
  ) {
    nextElement.remove();
  }

  return body.innerHTML;
};

const renderContentHtml = (content: string, title: string, description: string) => {
  const renderedContent = marked.parse(content || '') as string;
  const sanitizedContent = DOMPurify.sanitize(renderedContent);
  return DOMPurify.sanitize(stripDuplicateLeadContent(sanitizedContent, title, description));
};

const setSingleMeta = (selector: string, createAttributes: Record<string, string>, content: string) => {
  const tags = Array.from(document.head.querySelectorAll<HTMLMetaElement>(selector));
  const tag = tags[0] || document.createElement('meta');

  Object.entries(createAttributes).forEach(([key, value]) => tag.setAttribute(key, value));
  tag.setAttribute('content', content);

  if (!tags[0]) document.head.appendChild(tag);
  tags.slice(1).forEach((duplicate) => duplicate.remove());
};

const setCanonicalUrl = (href: string) => {
  const links = Array.from(document.head.querySelectorAll<HTMLLinkElement>('link[rel="canonical"]'));
  const link = links[0] || document.createElement('link');

  link.setAttribute('rel', 'canonical');
  link.setAttribute('href', href);

  if (!links[0]) document.head.appendChild(link);
  links.slice(1).forEach((duplicate) => duplicate.remove());
};

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBlogPost();
  }, [slug]);

  useEffect(() => {
    if (!post) return;

    const seoTitle = post.seo?.title || post.title;
    const seoDescription = post.seo?.description || post.description;
    const seoKeywords = post.seo?.keywords || post.tags.join(', ');
    const canonicalUrl = post.seo?.canonicalUrl || `${SITE_URL.replace(/\/+$/, '')}/blog/${post.slug}`;
    const datePublished = post.publishedAt || post.createdAt;
    const wordCount = post.content.trim().split(/\s+/).filter(Boolean).length;
    const faqEntities = (post.faq || [])
      .filter((item) => item.question && item.answer)
      .map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer
        }
      }));

    document.title = seoTitle;
    setSingleMeta('meta[name="description"]', { name: 'description' }, seoDescription);
    setSingleMeta('meta[name="keywords"]', { name: 'keywords' }, seoKeywords);
    setSingleMeta('meta[name="robots"]', { name: 'robots' }, 'index,follow,max-image-preview:large');
    setSingleMeta('meta[property="og:title"]', { property: 'og:title' }, seoTitle);
    setSingleMeta('meta[property="og:description"]', { property: 'og:description' }, seoDescription);
    setSingleMeta('meta[property="og:type"]', { property: 'og:type' }, 'article');
    setSingleMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    setSingleMeta('meta[property="og:site_name"]', { property: 'og:site_name' }, 'Kundlistar Studio');
    setSingleMeta('meta[property="article:published_time"]', { property: 'article:published_time' }, datePublished);
    setSingleMeta('meta[property="article:modified_time"]', { property: 'article:modified_time' }, post.updatedAt);
    setSingleMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, seoTitle);
    setSingleMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, seoDescription);
    setCanonicalUrl(canonicalUrl);

    document.getElementById('blog-structured-data')?.remove();
    const script = document.createElement('script');
    script.id = 'blog-structured-data';
    script.type = 'application/ld+json';
    const structuredDataGraph: Record<string, unknown>[] = [
      {
        '@type': 'BlogPosting',
        '@id': `${canonicalUrl}#article`,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': canonicalUrl
        },
        headline: post.title,
        description: seoDescription,
        abstract: post.aiSummary || seoDescription,
        datePublished,
        dateModified: post.updatedAt,
        inLanguage: post.language || 'en',
        articleSection: post.category,
        keywords: post.tags,
        wordCount,
        isAccessibleForFree: true,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['#article-headline', '#article-summary', '#article-key-takeaways']
        },
        author: {
          '@type': 'Person',
          name: post.author || 'Kundlistar Studio Editorial Team'
        },
        publisher: {
          '@type': 'Organization',
          name: 'Kundlistar Studio',
          url: SITE_URL,
          logo: {
            '@type': 'ImageObject',
            url: `${SITE_URL}/logo512.png`
          }
        }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: SITE_URL
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Blog',
            item: `${SITE_URL}/blog`
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: post.title,
            item: canonicalUrl
          }
        ]
      }
    ];

    if (faqEntities.length > 0) {
      structuredDataGraph.push({
        '@type': 'FAQPage',
        mainEntity: faqEntities
      });
    }

    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': structuredDataGraph
    });
    document.head.appendChild(script);

    return () => {
      document.getElementById('blog-structured-data')?.remove();
    };
  }, [post]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError('');

      if (!slug) {
        throw new Error('No slug provided');
      }

      const response = await fetch(`${API_BASE_URL}/blog/${slug}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('Blog post not found');
        } else {
          throw new Error('Failed to fetch blog post');
        }
        return;
      }

      const data = await response.json();
      setPost(data);

      // Fetch related posts
      fetchRelatedPosts(slug);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error fetching blog post');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedPosts = async (postSlug: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/blog/related/${postSlug}`);
      if (response.ok) {
        const data = await response.json();
        setRelatedPosts(data);
      }
    } catch (err) {
      console.error('Error fetching related posts:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDisplayDate(dateString);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error || 'Blog post not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ color: 'primary.main', fontWeight: 600 }}
        >
          Back to Blog
        </Button>
      </Container>
    );
  }

  return (
    <>
      <article>
        <Container maxWidth="md" sx={{ py: 6 }}>
          {/* Back Button */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/blog')}
            sx={{ mb: 4, color: 'primary.main', fontWeight: 600 }}
          >
            Back to Blog
          </Button>

          {/* Hero Section */}
          <Box sx={{ mb: 6 }}>
            {/* Category and Meta */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
              <Chip
                label={post.category}
                sx={{
                  backgroundColor: 'primary.main',
                  color: 'white',
                  textTransform: 'capitalize',
                  fontWeight: 600
                }}
              />
              <Box sx={{ display: 'flex', gap: 3, fontSize: '0.9rem', color: '#666', flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <DateRangeIcon sx={{ fontSize: '1.1rem' }} />
                  {formatDate(post.createdAt)}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: '1.1rem' }} />
                  {post.readingTime} min read
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityIcon sx={{ fontSize: '1.1rem' }} />
                  {post.views} views
                </Box>
              </Box>
            </Box>

            {/* Title */}
            <Typography
              id="article-headline"
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '1.8rem', md: '2.5rem' },
                fontWeight: 600,
                color: 'primary.main',
                mb: 3,
                lineHeight: 1.3
              }}
            >
              {post.title}
            </Typography>

            {/* Description */}
            <Typography id="article-summary" variant="body1" sx={{ fontSize: '1.1rem', color: '#666', mb: 4, fontStyle: 'italic' }}>
              {post.description}
            </Typography>

            {/* Author Info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <Avatar sx={{ width: 48, height: 48, backgroundColor: 'primary.main' }}>
                {post.author.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {post.author}
                </Typography>
                <Typography variant="caption" sx={{ color: '#999' }}>
                  {formatDate(post.createdAt)}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ mb: 6 }} />

          {post.aiSummary && (
            <Box
              component="aside"
              aria-labelledby="article-ai-summary-heading"
              sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}
            >
              <Typography id="article-ai-summary-heading" component="h2" variant="h5" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600 }}>
                Article summary
              </Typography>
              <Typography sx={{ lineHeight: 1.8 }}>{post.aiSummary}</Typography>
            </Box>
          )}

          {post.keyTakeaways && post.keyTakeaways.length > 0 && (
            <Box id="article-key-takeaways" component="section" aria-labelledby="article-key-takeaways-heading" sx={{ mb: 5 }}>
              <Typography id="article-key-takeaways-heading" component="h2" variant="h5" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 600 }}>
                Key takeaways
              </Typography>
              <Box component="ul" sx={{ pl: 3, my: 0 }}>
                {post.keyTakeaways.map((takeaway) => (
                  <Typography component="li" key={takeaway} sx={{ mb: 1, lineHeight: 1.7 }}>
                    {takeaway}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Blog Content */}
          <MarkdownContainer id="article-body" sx={{ mb: 8 }}>
            <Box dangerouslySetInnerHTML={{ __html: renderContentHtml(post.content, post.title, post.description) }} />
          </MarkdownContainer>

          {post.faq && post.faq.length > 0 && (
            <Box component="section" aria-labelledby="article-faq-heading" sx={{ mb: 6 }}>
              <Typography id="article-faq-heading" component="h2" variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
                Frequently asked questions
              </Typography>
              {post.faq.map((item) => (
                <Box key={item.question} sx={{ mb: 3 }}>
                  <Typography component="h3" variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    {item.question}
                  </Typography>
                  <Typography sx={{ lineHeight: 1.8 }}>{item.answer}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 6 }} />

          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <Box sx={{ mb: 6 }}>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {post.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={`#${tag}`}
                    variant="outlined"
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Container>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <Box sx={{ backgroundColor: '#f9f9f9', py: 8 }}>
            <Container maxWidth="md">
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: { xs: '1.5rem', md: '2rem' }
                }}
              >
                Related Articles
              </Typography>

              <Grid container spacing={3}>
                {relatedPosts.map((relatedPost) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={relatedPost._id}>
                    <Box
                      component={RouterLink}
                      to={`/blog/${relatedPost.slug}`}
                      onClick={() => window.scrollTo(0, 0)}
                      aria-label={`Read ${relatedPost.title}`}
                      sx={{ display: 'block', height: '100%', color: 'inherit', textDecoration: 'none' }}
                    >
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 1, color: 'primary.main', fontWeight: 600 }}>
                          {relatedPost.title}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                          {relatedPost.description}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', color: '#999' }}>
                          <span>{formatDate(relatedPost.createdAt)}</span>
                          <span>{relatedPost.readingTime} min</span>
                        </Box>
                      </CardContent>
                    </Card>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}
      </article>
    </>
  );
};

export default BlogPost;
